import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VotingButtons = ({ newsletterId, sectionId = null, initialUpvotes = 0, initialDownvotes = 0, initialUserVote = null, onVoteChange }) => {
  const [upvotes, setUpvotes] = useState(initialUpvotes);
  const [downvotes, setDownvotes] = useState(initialDownvotes);
  const [userVote, setUserVote] = useState(initialUserVote);
  const [loading, setLoading] = useState(false);

  const handleVote = async (voteType) => {
    if (loading) return;

    setLoading(true);
    try {
      // If clicking the same vote, remove it
      if (userVote === voteType) {
        const params = sectionId ? `?section_id=${sectionId}` : '';
        const response = await axios.delete(
          `${API}/newsletters/${newsletterId}/vote${params}`,
          { withCredentials: true }
        );
        setUpvotes(response.data.upvotes);
        setDownvotes(response.data.downvotes);
        setUserVote(null);
        toast.success("Vote removed");
      } else {
        // Add or change vote
        const response = await axios.post(
          `${API}/newsletters/${newsletterId}/vote`,
          { vote_type: voteType, section_id: sectionId },
          { withCredentials: true }
        );
        setUpvotes(response.data.upvotes);
        setDownvotes(response.data.downvotes);
        setUserVote(response.data.user_vote);
        toast.success(voteType === "up" ? "👍 Helpful!" : "Feedback noted");
      }
      
      if (onVoteChange) {
        onVoteChange();
      }
    } catch (error) {
      console.error("Error voting:", error);
      toast.error("Failed to record vote");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="voting-buttons">
      <Button
        data-testid="vote-up-btn"
        onClick={() => handleVote("up")}
        disabled={loading}
        variant={userVote === "up" ? "default" : "outline"}
        className={`flex items-center gap-2 ${
          userVote === "up" 
            ? "bg-green-600 hover:bg-green-700 text-white" 
            : "hover:bg-green-50 hover:border-green-300"
        }`}
      >
        <ThumbsUp className={`h-4 w-4 ${userVote === "up" ? "fill-current" : ""}`} />
        <span className="font-semibold">{upvotes}</span>
      </Button>

      <Button
        data-testid="vote-down-btn"
        onClick={() => handleVote("down")}
        disabled={loading}
        variant={userVote === "down" ? "default" : "outline"}
        className={`flex items-center gap-2 ${
          userVote === "down" 
            ? "bg-red-600 hover:bg-red-700 text-white" 
            : "hover:bg-red-50 hover:border-red-300"
        }`}
      >
        <ThumbsDown className={`h-4 w-4 ${userVote === "down" ? "fill-current" : ""}`} />
        <span className="font-semibold">{downvotes}</span>
      </Button>
    </div>
  );
};

export default VotingButtons;
